import React, { useState, useEffect } from 'react';
import { 
  Settings, Calculator, CreditCard, User, Save,
  Loader2, CheckCircle, Target, Percent, PiggyBank,
  Building, FileText, AlertCircle
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';
import { toast } from 'sonner';

const AdminConfig = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('fiscal');
  
  // Config state
  const [config, setConfig] = useState({
    annual_goal: 120000,
    vat_rate: 21,
    irpf_rate: 20,
    monthly_fixed_expenses: 2000,
    security_fund_current: 0,
    business_name: '',
    nif: '',
    address: ''
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/admin/finanzas/dashboard');
      setConfig({
        annual_goal: res.data.projection?.annual_goal || 120000,
        vat_rate: 21,
        irpf_rate: 20,
        monthly_fixed_expenses: res.data.security_fund?.monthly_fixed_expenses || 2000,
        security_fund_current: res.data.security_fund?.current || 0,
        business_name: 'Escuela de Consciencia Akáshica',
        nif: '',
        address: ''
      });
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/admin/finanzas/config', {
        annual_goal: config.annual_goal,
        monthly_fixed_expenses: config.monthly_fixed_expenses,
        security_fund_current: config.security_fund_current
      });
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const tabs = [
    { id: 'fiscal', label: 'Datos Fiscales', icon: Calculator },
    { id: 'objetivos', label: 'Objetivos', icon: Target },
    { id: 'conexiones', label: 'Conexiones', icon: CreditCard },
  ];

  if (loading) {
    return (
      <div className="p-6 lg:p-8 min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-4" />
          <p className="text-slate-500">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900" data-testid="config-title">
          Configuración
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Ajustes del sistema, datos fiscales y conexiones
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200 pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === tab.id 
                ? 'bg-slate-900 text-white' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Fiscal Tab */}
      {activeTab === 'fiscal' && (
        <div className="space-y-6">
          {/* Business Info */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Building className="w-5 h-5 text-indigo-500" />
              Datos del Negocio
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nombre del negocio
                </label>
                <Input
                  value={config.business_name}
                  onChange={(e) => setConfig(prev => ({ ...prev, business_name: e.target.value }))}
                  placeholder="Nombre comercial"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  NIF/CIF
                </label>
                <Input
                  value={config.nif}
                  onChange={(e) => setConfig(prev => ({ ...prev, nif: e.target.value }))}
                  placeholder="12345678A"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Dirección fiscal
                </label>
                <Input
                  value={config.address}
                  onChange={(e) => setConfig(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Calle, número, ciudad, CP"
                />
              </div>
            </div>
          </div>

          {/* Tax Rates */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Percent className="w-5 h-5 text-amber-500" />
              Tipos Impositivos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  IVA General (%)
                </label>
                <Input
                  type="number"
                  value={config.vat_rate}
                  onChange={(e) => setConfig(prev => ({ ...prev, vat_rate: parseInt(e.target.value) || 0 }))}
                />
                <p className="text-xs text-slate-400 mt-1">Se reserva automáticamente para Modelo 303</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  IRPF Trimestral (%)
                </label>
                <Input
                  type="number"
                  value={config.irpf_rate}
                  onChange={(e) => setConfig(prev => ({ ...prev, irpf_rate: parseInt(e.target.value) || 0 }))}
                />
                <p className="text-xs text-slate-400 mt-1">Se reserva automáticamente para Modelo 130</p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-amber-50 rounded-lg">
              <p className="text-sm text-amber-700">
                <strong>Nota:</strong> Estos porcentajes se aplican automáticamente en el cálculo de reservas 
                del Centro de Mando Financiero.
              </p>
            </div>
          </div>

          {/* Security Fund */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <PiggyBank className="w-5 h-5 text-purple-500" />
              Fondo de Seguridad
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Gastos fijos mensuales (€)
                </label>
                <Input
                  type="number"
                  value={config.monthly_fixed_expenses}
                  onChange={(e) => setConfig(prev => ({ ...prev, monthly_fixed_expenses: parseInt(e.target.value) || 0 }))}
                />
                <p className="text-xs text-slate-400 mt-1">Incluye alquiler, suministros, suscripciones, etc.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Fondo actual acumulado (€)
                </label>
                <Input
                  type="number"
                  value={config.security_fund_current}
                  onChange={(e) => setConfig(prev => ({ ...prev, security_fund_current: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="p-4 bg-amber-50 rounded-lg text-center">
                <p className="text-xs text-amber-600">Objetivo 3 meses (mínimo)</p>
                <p className="font-semibold text-amber-700">{formatCurrency(config.monthly_fixed_expenses * 3)}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <p className="text-xs text-green-600">Objetivo 6 meses (ideal)</p>
                <p className="font-semibold text-green-700">{formatCurrency(config.monthly_fixed_expenses * 6)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Objetivos Tab */}
      {activeTab === 'objetivos' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-indigo-500" />
              Objetivo Anual 2026
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Facturación anual objetivo (€)
                </label>
                <Input
                  type="number"
                  value={config.annual_goal}
                  onChange={(e) => setConfig(prev => ({ ...prev, annual_goal: parseInt(e.target.value) || 0 }))}
                  className="text-lg"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Este objetivo se usa en todo el Centro de Mando
                </p>
              </div>
              <div className="bg-indigo-50 rounded-lg p-4">
                <p className="text-sm text-indigo-700 mb-2">Con este objetivo necesitas:</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-indigo-600">Media mensual:</span>
                    <span className="font-semibold text-indigo-800">{formatCurrency(config.annual_goal / 12)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-indigo-600">Media semanal:</span>
                    <span className="font-semibold text-indigo-800">{formatCurrency(config.annual_goal / 52)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-indigo-600">Media diaria:</span>
                    <span className="font-semibold text-indigo-800">{formatCurrency(config.annual_goal / 365)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Model visualization */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100">
            <h3 className="font-semibold text-purple-900 mb-4">Modelo Autónomo 2026</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-white/60 rounded-lg p-4">
                <p className="text-3xl font-bold text-purple-700">{formatCurrency(config.annual_goal)}</p>
                <p className="text-sm text-purple-600">Facturación anual</p>
              </div>
              <div className="bg-white/60 rounded-lg p-4">
                <p className="text-3xl font-bold text-purple-700">30h</p>
                <p className="text-sm text-purple-600">Horas/semana</p>
              </div>
              <div className="bg-white/60 rounded-lg p-4">
                <p className="text-3xl font-bold text-purple-700">100€</p>
                <p className="text-sm text-purple-600">€/hora objetivo</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conexiones Tab */}
      {activeTab === 'conexiones' && (
        <div className="space-y-6">
          {/* Stripe */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Stripe</h3>
                  <p className="text-sm text-slate-400">Pagos con tarjeta y SEPA</p>
                </div>
              </div>
              <span className="px-3 py-1 bg-slate-100 text-slate-500 text-xs rounded-full">Próximamente</span>
            </div>
            <p className="text-sm text-slate-500">
              Conecta tu cuenta de Stripe para procesar pagos automáticos con tarjeta y domiciliación SEPA.
            </p>
          </div>

          {/* PayPal */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">PayPal</h3>
                  <p className="text-sm text-slate-400">Pagos PayPal</p>
                </div>
              </div>
              <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs rounded-full flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Sandbox
              </span>
            </div>
            <p className="text-sm text-slate-500">
              PayPal está conectado en modo Sandbox (pruebas). Cambiar a producción cuando estés listo.
            </p>
          </div>

          {/* Email */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Email (Resend)</h3>
                  <p className="text-sm text-slate-400">Emails transaccionales</p>
                </div>
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Conectado
              </span>
            </div>
            <p className="text-sm text-slate-500">
              Resend está configurado para enviar emails de confirmación, facturas y notificaciones.
            </p>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <Button 
          onClick={saveConfig} 
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Guardar configuración
        </Button>
      </div>
    </div>
  );
};

export default AdminConfig;
